import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { audit } from "../lib/audit";
import {
  LayoutDashboard,
  Footprints,
  AlertTriangle,
  Users,
  Repeat,
  LogOut,
  X,
  Mail,
  Phone,
  ShieldCheck,
  Building2,
  Briefcase,
  Edit3,
  Save,
  ClipboardList,
  FileText,
  ClipboardCheck,
  Menu,
  ChevronRight,
  CalendarDays,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { appConfig, usuarios } from "../data/mock";

// Itens ocultos temporariamente — reativar quando necessário:
// { to: "/ocorrencias",    icon: AlertTriangle, label: "Ocorrências"        },
// { to: "/passagem-turno", icon: Repeat,        label: "Passagens de Turno" },
// { to: "/sindico",        icon: Users,         label: "Vigilantes"         },
// { to: "/escala",         icon: CalendarDays,  label: "Escala 7 dias"      },
const mainNavItems = [
  { to: "/dashboard",  icon: LayoutDashboard, label: "Painel"     },
  { to: "/rondas",     icon: Footprints,      label: "Rondas"     },
  { to: "/registros",  icon: ClipboardList,   label: "Registros"  },
  { to: "/relatorios", icon: FileText,        label: "Relatórios" },
];

const supervisorNavItems = [
  { to: "/supervisor/ocorrencias", icon: ClipboardCheck, label: "Aprovar Ocorrências" },
  { to: "/supervisor/efetivo",     icon: Users,          label: "Efetivo Atual"       },
  { to: "/supervisor/passagem",    icon: Repeat,         label: "Passagem de Plantão" },
];

// Itens ocultos temporariamente para síndico — reativar quando necessário:
// { to: "/ocorrencias", icon: AlertTriangle, label: "Ocorrências" },
// { to: "/sindico",     icon: Users,         label: "Vigilantes"  },
const sindicoNavItems = [
  { to: "/dashboard",  icon: LayoutDashboard, label: "Painel"     },
  { to: "/rondas",     icon: Footprints,      label: "Rondas"     },
  { to: "/registros",  icon: ClipboardList,   label: "Registros"  },
  { to: "/relatorios", icon: FileText,        label: "Relatórios" },
];

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
};

export const DesktopLayout = ({ children, title, subtitle, actions }) => {
  const { user, logout } = useApp();
  const navigate = useNavigate();

  const [menuAberto, setMenuAberto]       = useState(false);
  const [perfilAberto, setPerfilAberto]   = useState(false);
  const [editandoPerfil, setEditandoPerfil] = useState(false);
  const [perfilLocal, setPerfilLocal]     = useState(null);

  // Perfil do usuário logado a partir do mock (liberty pode ser "sindico" ou "supervisor")
  const usuarioDataEarly = usuarios.find((u) => u.id === user?.usuarioId);
  const perfilMockEarly  = usuarioDataEarly?.perfil;

  const getNomeUsuario   = () => user?.nome || "Usuário";
  const getPerfilUsuario = () => {
    if (user?.role === "liberty" && perfilMockEarly === "sindico")    return "Síndico";
    if (user?.role === "liberty" && perfilMockEarly === "supervisor")  return "Supervisor Liberty";
    const labels = {
      sindico: "Síndico", admin: "Administrador", supervisor: "Supervisor",
      vigilante: "Vigilante", liberty: "Liberty Place", esquematiza: "Grupo Esquematiza",
    };
    return labels[user?.role] || "Usuário";
  };
  const getEmailUsuario    = () => user?.email || "";
  const getTelefoneUsuario = () => {
    if (user?.role === "sindico") return "21 3171-1859 / 1984 / 1988";
    if (user?.role === "admin")   return "Contato administrativo";
    return "Contato não informado";
  };
  const getContatoEmergencia = () => {
    if (user?.role === "sindico") return "Administração Liberty Place";
    if (user?.role === "admin")   return "Central Grupo Esquematiza";
    return "Supervisor operacional";
  };

  const perfilStorageKey = `perfil_usuario_${user?.id || user?.email || user?.role || "padrao"}`;
  const perfilPadrao = {
    nomeCompleto: getNomeUsuario(), perfil: getPerfilUsuario(), email: getEmailUsuario(),
    telefone: getTelefoneUsuario(), contatoEmergencia: getContatoEmergencia(),
    condominio: appConfig.condominioPadrao, funcao: getPerfilUsuario(), observacoes: "",
  };

  useEffect(() => {
    const perfilSalvo = localStorage.getItem(perfilStorageKey);
    setPerfilLocal(perfilSalvo ? JSON.parse(perfilSalvo) : perfilPadrao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfilStorageKey]);

  const atualizarCampoPerfil = (campo, valor) =>
    setPerfilLocal((atual) => ({ ...(atual || perfilPadrao), [campo]: valor }));

  const salvarPerfil = () => {
    localStorage.setItem(perfilStorageKey, JSON.stringify(perfilLocal));
    setEditandoPerfil(false);
  };

  const cancelarEdicao = () => {
    const s = localStorage.getItem(perfilStorageKey);
    setPerfilLocal(s ? JSON.parse(s) : perfilPadrao);
    setEditandoPerfil(false);
  };

  const sairDoSistema = () => {
    audit({ usuario_nome: user?.nome, usuario_role: user?.role, acao: "logout", modulo: "acesso", descricao: "Usuário encerrou sessão" });
    logout();
    navigate("/login");
  };

  const nomeExibicao   = perfilLocal?.nomeCompleto || getNomeUsuario();
  const perfilExibicao = perfilLocal?.perfil       || getPerfilUsuario();
  const primeiroNome   = nomeExibicao.split(" ")[0].replace(/\(.*\)/, "").trim();

  const initials = nomeExibicao
    .replace(/\(.*\)/g, "")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // Síndico vê nav reduzida; demais usuários (esquematiza, admin etc.) veem tudo
  const isSindico = user?.role === "liberty" && perfilMockEarly === "sindico";
  const mainNav   = isSindico ? sindicoNavItems : mainNavItems;
  // Supervisão oculta temporariamente — reativar com:
  // (user?.role === "esquematiza" || (user?.role === "liberty" && perfilMockEarly === "supervisor")) ? supervisorNavItems : []
  const supNav = [];

  const SidebarLink = ({ item }) => {
    const Icon = item.icon;
    return (
      <NavLink
        to={item.to}
        onClick={() => setMenuAberto(false)}
        data-testid={`desk-nav-${item.to.replace("/", "")}`}
        className={({ isActive }) =>
          `group flex items-center gap-3 px-3 h-10 rounded-xl text-[13px] font-medium transition-all duration-150 ${
            isActive
              ? "text-[#3B82F6]"
              : "text-white/45 hover:text-white/80"
          }`
        }
        style={({ isActive }) =>
          isActive
            ? { background: "rgba(59,130,246,0.12)", border: "none" }
            : {}
        }
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="truncate">{item.label}</span>
      </NavLink>
    );
  };

  return (
    <div
      className="min-h-screen flex text-white"
      style={{ background: "#050816", fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}
    >
      {/* ── Overlay mobile ── */}
      {menuAberto && (
        <div className="fixed inset-0 z-30 md:hidden" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setMenuAberto(false)} />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={`fixed md:relative inset-y-0 left-0 z-40 flex flex-col transition-transform duration-300 ${
          menuAberto ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        style={{
          width: 260,
          minWidth: 260,
          background: "#08111F",
          borderRight: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        {/* Logo */}
        <div className="px-5 py-5 flex-none" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <button
            onClick={() => setMenuAberto(false)}
            className="md:hidden absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <X className="w-4 h-4 text-white/60" />
          </button>
          <img
            src={appConfig.logoUrl}
            alt={appConfig.nomeComercial}
            className="h-8 object-contain"
          />
        </div>

        {/* Nav principal */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-0.5">
          <p className="text-[10px] font-semibold tracking-[0.12em] uppercase px-3 mb-2" style={{ color: "rgba(255,255,255,0.25)" }}>
            Principal
          </p>
          {mainNav.map((item) => <SidebarLink key={item.to} item={item} />)}

          {supNav.length > 0 && (
            <>
              <div className="pt-4 pb-1">
                <p className="text-[10px] font-semibold tracking-[0.12em] uppercase px-3 mb-2" style={{ color: "rgba(255,255,255,0.25)" }}>
                  Supervisão
                </p>
              </div>
              {supNav.map((item) => <SidebarLink key={item.to} item={item} />)}
            </>
          )}
        </nav>

        {/* Logout */}
        <div className="px-3 pb-4 pt-3 flex-none" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <button
            data-testid="btn-logout-desktop"
            onClick={sairDoSistema}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] transition-all"
            style={{ color: "rgba(255,255,255,0.35)" }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.7)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.35)"; e.currentTarget.style.background = "transparent"; }}
          >
            <LogOut className="w-3.5 h-3.5" />
            Sair do sistema
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <header
          className="sticky top-0 z-30 flex-none"
          style={{
            background: "rgba(5,8,22,0.88)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div className="px-5 md:px-8 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMenuAberto(true)}
                className="md:hidden w-9 h-9 rounded-xl flex items-center justify-center transition"
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <Menu className="w-4.5 h-4.5" />
              </button>

              <div>
                <h1 className="text-[20px] font-bold text-white leading-tight tracking-tight">
                  {getGreeting()}, {primeiroNome} 👋
                </h1>
                <p className="text-[13px] mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
                  {subtitle || "Visão executiva e operacional do plantão."}
                </p>
              </div>
            </div>

            <div className="relative flex items-center gap-2">
              {actions}

              <button
                type="button"
                onClick={() => setPerfilAberto(!perfilAberto)}
                className="hidden md:flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.07)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.04)")}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                  style={{ background: "rgba(59,130,246,0.18)", color: "#3B82F6" }}
                >
                  {initials}
                </div>
                <div className="hidden xl:block text-left">
                  <p className="text-[13px] font-semibold text-white leading-tight">{nomeExibicao}</p>
                  <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.4)" }}>{perfilExibicao}</p>
                </div>
              </button>

              {/* Profile panel */}
              {perfilAberto && (
                <div
                  className="absolute right-0 top-[52px] w-[380px] rounded-2xl z-50 overflow-hidden shadow-2xl"
                  style={{ background: "#08111F", border: "1px solid rgba(255,255,255,0.09)" }}
                >
                  <div className="px-5 py-4 flex items-start justify-between gap-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                    <div>
                      <p className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: "rgba(255,255,255,0.3)" }}>Perfil profissional</p>
                      <h3 className="text-[17px] font-bold mt-1 text-white">{nomeExibicao}</h3>
                      <p className="text-[13px] mt-0.5" style={{ color: "rgba(59,130,246,0.9)" }}>{perfilExibicao} · <span translate="no">{appConfig.condominioPadrao}</span></p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setPerfilAberto(false); setEditandoPerfil(false); }}
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition"
                      style={{ background: "rgba(255,255,255,0.06)" }}
                    >
                      <X className="w-4 h-4 text-white/60" />
                    </button>
                  </div>

                  <div className="p-4 space-y-2.5 max-h-[60vh] overflow-y-auto">
                    {!editandoPerfil ? (
                      <>
                        <InfoRow icon={Mail}       label="E-mail"                value={perfilLocal?.email} />
                        <InfoRow icon={Phone}      label="Contato"               value={perfilLocal?.telefone} />
                        <InfoRow icon={ShieldCheck} label="Contato de emergência" value={perfilLocal?.contatoEmergencia} />
                        <InfoRow icon={Building2}  label="Condomínio"            value={perfilLocal?.condominio} noTranslate />
                        <InfoRow icon={Briefcase}  label="Função"                value={perfilLocal?.funcao} />
                        {perfilLocal?.observacoes && (
                          <div className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                            <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "rgba(255,255,255,0.3)" }}>Observações</p>
                            <p className="text-[13px] text-white/70">{perfilLocal.observacoes}</p>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setEditandoPerfil(true)}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-medium transition-all"
                          style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)", color: "#60a5fa" }}
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          Editar informações
                        </button>
                      </>
                    ) : (
                      <>
                        <FieldEdit label="Nome completo"         value={perfilLocal?.nomeCompleto || ""}     onChange={(v) => atualizarCampoPerfil("nomeCompleto",      v)} />
                        <FieldEdit label="E-mail"                value={perfilLocal?.email || ""}            onChange={(v) => atualizarCampoPerfil("email",             v)} />
                        <FieldEdit label="Telefone / WhatsApp"   value={perfilLocal?.telefone || ""}         onChange={(v) => atualizarCampoPerfil("telefone",          v)} />
                        <FieldEdit label="Contato de emergência" value={perfilLocal?.contatoEmergencia || ""} onChange={(v) => atualizarCampoPerfil("contatoEmergencia", v)} />
                        <FieldEdit label="Condomínio"            value={perfilLocal?.condominio || ""}       onChange={(v) => atualizarCampoPerfil("condominio",        v)} />
                        <FieldEdit label="Função"                value={perfilLocal?.funcao || ""}           onChange={(v) => atualizarCampoPerfil("funcao",            v)} />
                        <div>
                          <label className="text-[11px] font-medium block mb-1.5" style={{ color: "rgba(255,255,255,0.4)" }}>Observações</label>
                          <textarea
                            value={perfilLocal?.observacoes || ""}
                            onChange={(e) => atualizarCampoPerfil("observacoes", e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2.5 rounded-xl text-[13px] text-white resize-none focus:outline-none"
                            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.85)" }}
                            onFocus={(e) => (e.target.style.borderColor = "rgba(59,130,246,0.5)")}
                            onBlur={(e)  => (e.target.style.borderColor = "rgba(255,255,255,0.09)")}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2 pt-1">
                          <button
                            type="button"
                            onClick={cancelarEdicao}
                            className="px-4 py-2.5 rounded-xl text-[13px] font-medium transition-all"
                            style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)" }}
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={salvarPerfil}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all"
                            style={{ background: "#10B981" }}
                          >
                            <Save className="w-3.5 h-3.5" />
                            Salvar
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="px-4 pb-4 pt-2" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
                    <button
                      type="button"
                      onClick={sairDoSistema}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-medium transition-all"
                      style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.18)", color: "#f87171" }}
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      Sair do sistema
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 px-5 md:px-8 py-8 max-w-[1600px]">
          {children}
        </div>
      </main>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, noTranslate }) => (
  <div
    className="flex items-start gap-3 px-3 py-2.5 rounded-xl"
    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
  >
    <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "rgba(255,255,255,0.35)" }} />
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: "rgba(255,255,255,0.3)" }}>{label}</p>
      <p className="text-[13px] text-white/75 break-all" translate={noTranslate ? "no" : undefined}>
        {value || "Não informado"}
      </p>
    </div>
  </div>
);

const FieldEdit = ({ label, value, onChange }) => (
  <div>
    <label className="text-[11px] font-medium block mb-1.5" style={{ color: "rgba(255,255,255,0.4)" }}>{label}</label>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2.5 rounded-xl text-[13px] focus:outline-none"
      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.85)" }}
      onFocus={(e) => (e.target.style.borderColor = "rgba(59,130,246,0.5)")}
      onBlur={(e)  => (e.target.style.borderColor = "rgba(255,255,255,0.09)")}
    />
  </div>
);
